// src/components/VideoClipModal.js
/**
 * VideoClipModal — 视频快剪弹窗（全屏大面板，类似 PS / 快照面板尺寸 90vw×85vh）
 *
 * ┌────────────────────────────────────────────────────────────────┐
 * │  功能概述                                                      │
 * │  在时间轴上拖动入点(绿)/出点(红)，导出 OSS 裁剪片段，           │
 * │  生成新视频节点。时间轴背景显示音频波形辅助定位。                 │
 * └────────────────────────────────────────────────────────────────┘
 *
 * Props:
 *   @param {string}   previewUrl  - 用于本地预览的 blob:// URL（可跨域访问）
 *   @param {string|null} sourceUrl - OSS 视频 URL（仅用于参考，不再发送给后端下载）
 *   @param {Function} onClose     - 关闭弹窗回调
 *   @param {Function} onExportAsync - 异步导出回调，参数为 (inPoint, outPoint)，
 *                                     由父组件（VideoNodeView）负责实际裁剪和上传
 *
 * 工作流（异步非阻塞）：
 *   1. 前端：加载视频 → Web Audio API 提取音频波形 → 绘制到 Canvas
 *   2. 前端：用户拖动入出点或按 I/O 键设置裁剪范围
 *   3. 前端：点击「导出片段」→ 立即关闭弹窗、回到画布
 *   4. 父组件：创建「裁剪中」loading 节点 → 后台 FFmpeg.wasm 裁剪 → 上传 → 更新节点
 *   ※ 用户无需等待裁剪完成即可继续其他操作
 *
 * 关键设计决策：
 *   ① 拖拽使用 ref（非 useState）跟踪状态，mouseDown 时直接注册 window 事件，
 *     避免 React 渲染周期延迟导致 mousemove/mouseup 监听器挂载不及时。
 *   ② 最小裁剪间隔 0.1 秒（≈3帧@30fps），后端帧精确重编码保证精度。
 *   ③ 时间显示精度为厘秒（00:00.00），满足逐帧定位需求。
 *   ④ 通过 createPortal 渲染到 document.body，避免被父组件 overflow 裁剪。
 *   ⑤ 导出期间视频预览区显示全屏旋转动画 + "正在处理..." 文字。
 *
 * 快捷键：
 *   空格   = 播放/暂停（到出点自动跳回入点）
 *   I      = 将当前播放头位置设为入点
 *   O      = 将当前播放头位置设为出点
 *   Esc    = 关闭弹窗
 *
 * ⚠️ 导出需要视频已加载完成（duration > 0）。
 *    无论视频是已上传 OSS 还是本地文件，均可使用快剪。
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Play, Pause, Scissors } from 'lucide-react';
import { getSharedAudioContext } from '../services/audioContextService';
// 裁剪逻辑已移至 VideoNodeView（异步非阻塞），本弹窗只负责 UI 交互

// ── 颜色主题（VS Code / 快照面板统一暗色风格）────────────────────────────────

const C = {
  bg: 'var(--ed-viewport)',   // 面板主背景（深色=#1e1e1e）
  panel: 'var(--ed-panel)',   // 底部控制栏背景（深色=#252526）
  surface: 'var(--ed-header)', // 时间轴/按钮表面（深色=#2d2d30）
  border: 'var(--ed-border)', // 边框（深色=#3e3e42）
  accent: '#0078d7',          // 主色调（导出按钮、选中高亮）— 语义蓝，保持
  inColor: '#22c55e',         // 入点颜色（绿）— 功能色，保持
  outColor: '#ef4444',        // 出点颜色（红）— 功能色，保持
  playhead: '#f59e0b',        // 播放头颜色（橙）— 功能色，保持
  selectedBg: 'rgba(0,120,215,0.15)',    // 入出点间高亮背景 — 选区色，保持
  selectedBorder: 'rgba(0,120,215,0.6)', // 入出点间高亮边框 — 选区色，保持
  text: 'var(--ed-text)',     // 普通文字（深色=#cccccc）
  textBright: 'var(--text-body)', // 高亮文字（深色=#e2e8f0）
  textMuted: 'var(--ed-muted)', // 次要文字（深色=#808080）
  hoverBg: 'var(--ed-hover)', // 悬停背景（深色=#37373d）
};

// ── 工具函数 ─────────────────────────────────────────────────────────────────

/**
 * 格式化秒数为 MM:SS.cc（厘秒精度）
 * @param {number} seconds - 秒数，可带小数
 * @returns {string} 如 "01:23.45"
 *
 * 精度说明：2 位厘秒 = 0.01s 精度，足够表示单帧（@30fps 一帧 ≈ 0.033s）
 */
function fmt(seconds) {
  if (!seconds || isNaN(seconds)) return '00:00.00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  const cs = Math.floor((seconds % 1) * 100);
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}.${cs.toString().padStart(2, '0')}`;
}

// ── 主组件 ───────────────────────────────────────────────────────────────────

export default function VideoClipModal({ previewUrl, sourceUrl, onClose, onExportAsync }) {
  /* ── DOM Refs ───────────────────────────────────────────────────────────── */
  const videoRef = useRef(null);       // <video> 元素
  const timelineRef = useRef(null);    // 时间轴 div（用于坐标→时间映射）
  const waveCanvasRef = useRef(null);  // 音频波形 <canvas>

  /* ── 视频播放状态 ────────────────────────────────────────────────────────── */
  const [duration, setDuration] = useState(0);     // 视频总时长（秒）
  const [currentTime, setCurrentTime] = useState(0); // 当前播放位置（秒）
  const [isPlaying, setIsPlaying] = useState(false); // 是否正在播放

  /* ── 入出点（秒，浮点数，厘秒精度）───────────────────────────────────────── */
  const [inPoint, setInPoint] = useState(0);       // 裁剪起始时间
  const [outPoint, setOutPoint] = useState(0);     // 裁剪结束时间

  /* ── 音频波形数据（Float32Array，每个值 0-1，约 800 个采样点）───────────── */
  const [waveform, setWaveform] = useState(null);

  /* ── 导出状态 ────────────────────────────────────────────────────────────── */

  /* ── 拖拽状态（ref 模式）───────────────────────────────────────────────────
   * 为什么用 ref 而非 useState：
   *   useState 更新后需要等下一次渲染才能在 useEffect 中挂载 mousemove 事件，
   *   导致拖拽开始的第一帧丢失鼠标移动。ref 值同步变更，mouseDown 回调中
   *   直接注册 window 事件，零延迟。
   */
  const dragRef = useRef(null);        // 当前拖拽目标：'in' | 'out' | 'playhead' | null
  const inRef = useRef(0);             // inPoint 的实时镜像（拖拽中读取）
  const outRef = useRef(0);            // outPoint 的实时镜像
  const durRef = useRef(0);            // duration 的实时镜像

  // 同步 ref ← state（state 变化时立即更新对应 ref）
  useEffect(() => { inRef.current = inPoint; }, [inPoint]);
  useEffect(() => { outRef.current = outPoint; }, [outPoint]);
  useEffect(() => { durRef.current = duration; }, [duration]);

  /**
   * 是否可以导出
   * 条件：previewUrl 存在 且 视频已加载（duration > 0）
   * 无论视频是已上传 OSS 还是本地文件，均可导出
   * （前端直接 POST blob 到后端，后端裁剪完再上传 OSS）
   */
  const canExport = !!previewUrl && duration > 0;

  // ── 视频事件回调 ────────────────────────────────────────────────────────────

  /** 视频元数据加载完成 → 初始化 duration / inPoint / outPoint */
  const handleLoadedMetadata = () => {
    const dur = videoRef.current?.duration || 0;
    setDuration(dur);
    setInPoint(0);
    setOutPoint(dur);    // 初始状态：选中整段视频
  };

  /** 播放过程中持续更新 currentTime；到达出点时自动暂停 */
  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const t = videoRef.current.currentTime;
    setCurrentTime(t);
    // 播放到出点 → 停止（确保不会播放超出选择范围）
    if (t >= outRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = outRef.current;
      setIsPlaying(false);
    }
  };

  /**
   * 播放/暂停切换
   * 特殊逻辑：播放头在出点附近（≤0.05s）或出点之后时，
   *           按空格会自动跳回入点开始播放（避免卡在出点最后一帧）
   */
  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused || v.ended) {
      if (v.currentTime < inRef.current || v.currentTime >= outRef.current - 0.05) {
        v.currentTime = inRef.current;
      }
      v.play().catch(() => {});
      setIsPlaying(true);
    } else {
      v.pause();
      setIsPlaying(false);
    }
  }, []);

  /** 跳转到指定时间（clamp 到 0 ~ duration） */
  const seekTo = useCallback((t) => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, Math.min(durRef.current, t));
      setCurrentTime(videoRef.current.currentTime);
    }
  }, []);

  // ── 时间轴拖拽（ref-based 实现，无 React 渲染延迟） ────────────────────────

  /**
   * 将鼠标 clientX 坐标映射为视频时间（秒）
   * 基于时间轴 div 的 getBoundingClientRect，ratio ∈ [0, 1] → time ∈ [0, duration]
   */
  const getTimeFromX = useCallback((clientX) => {
    if (!timelineRef.current || durRef.current <= 0) return 0;
    const rect = timelineRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return ratio * durRef.current;
  }, []);

  /**
   * 开始拖拽：注册 window 级 mousemove/mouseup 事件
   * @param {MouseEvent} e    - mousedown 原生事件
   * @param {'in'|'out'|'playhead'} type - 拖拽目标
   *
   * 拖拽期间实时更新 inPoint/outPoint/playhead，同时同步 ref 值。
   * mouseup 时清理事件监听器。
   */
  const startDrag = useCallback((e, type) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = type;
    const t = getTimeFromX(e.clientX);

    // MIN_GAP: 最小入出点间隔（0.1 秒 ≈ 约 3 帧 @30fps）
    const MIN_GAP = 0.1;
    if (type === 'in') {
      const v = Math.max(0, Math.min(t, outRef.current - MIN_GAP));
      setInPoint(v); seekTo(v);
    } else if (type === 'out') {
      const v = Math.max(inRef.current + MIN_GAP, Math.min(t, durRef.current));
      setOutPoint(v); seekTo(v);
    } else {
      seekTo(t);
    }

    const onMove = (ev) => {
      const tt = getTimeFromX(ev.clientX);
      if (dragRef.current === 'in') {
        const v = Math.max(0, Math.min(tt, outRef.current - MIN_GAP));
        setInPoint(v); inRef.current = v; seekTo(v);
      } else if (dragRef.current === 'out') {
        const v = Math.max(inRef.current + MIN_GAP, Math.min(tt, durRef.current));
        setOutPoint(v); outRef.current = v; seekTo(v);
      } else {
        seekTo(tt);
      }
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [getTimeFromX, seekTo]);

  const handleBgMouseDown = useCallback((e) => {
    startDrag(e, 'playhead');
  }, [startDrag]);

  /* ── 音频波形提取（Web Audio API）────────────────────────────────────────────
   *
   * 流程：
   *   1. fetch previewUrl → ArrayBuffer
   *   2. AudioContext.decodeAudioData → AudioBuffer（提取音频轨道）
   *   3. getChannelData(0) → Float32Array（第一声道 PCM 采样）
   *   4. 降采样：将数百万个采样分成 ~800 个块，取每块平均绝对值
   *   5. 归一化到 0-1 范围 → setWaveform(peaks)
   *
   * 如果视频没有音频轨道，decodeAudioData 会抛异常，静默处理。
   * useEffect 通过 cancelled 标志防止组件卸载后写入 state。
   */

  useEffect(() => {
    if (!previewUrl) return;
    let cancelled = false;

    (async () => {
      try {
        const resp = await fetch(previewUrl);
        if (cancelled) return;
        const arrayBuf = await resp.arrayBuffer();
        if (cancelled) return;

        // 🔊 使用全局单例 AudioContext，避免浏览器 6 个上限被耗尽
        const audioCtx = getSharedAudioContext();
        let audioBuf;
        try {
          audioBuf = await audioCtx.decodeAudioData(arrayBuf);
        } catch {
          return;  // 视频无音轨，静默退出
        }
        if (cancelled) return;

        const raw = audioBuf.getChannelData(0);  // 第一声道 PCM

        // 降采样到 800 个峰值柱（覆盖 90vw 宽的时间轴绰绰有余）
        const BARS = 800;
        const blockSize = Math.floor(raw.length / BARS) || 1;
        const peaks = new Float32Array(BARS);
        for (let i = 0; i < BARS; i++) {
          let sum = 0;
          const offset = i * blockSize;
          for (let j = 0; j < blockSize; j++) {
            sum += Math.abs(raw[offset + j] || 0);
          }
          peaks[i] = sum / blockSize;
        }

        // 归一化到 [0, 1]（最大峰值 = 1）
        const maxPeak = Math.max(...peaks) || 1;
        for (let i = 0; i < BARS; i++) peaks[i] /= maxPeak;

        if (!cancelled) setWaveform(peaks);
        // 单例 AudioContext 不关闭，供后续复用
      } catch (e) {
        console.warn('[VideoClip] 波形提取失败:', e.message);
      }
    })();

    return () => { cancelled = true; };
  }, [previewUrl]);

  /* ── 绘制波形到 Canvas ────────────────────────────────────────────────────────
   *
   * 当 waveform 数据就绪后，使用 Canvas 2D 绘制对称柱状波形：
   *   - HiDPI 适配：canvas 实际像素 = CSS 像素 × devicePixelRatio
   *   - 每个柱子宽度 = 时间轴宽度 / 800（柱数）
   *   - 对称绘制：从 midY 向上下延伸，幅度 = peak × midY × 0.85
   *   - 颜色：半透明蓝色 rgba(100,160,255,0.35)，不干扰入出点手柄阅读
   */

  useEffect(() => {
    const canvas = waveCanvasRef.current;
    if (!canvas || !waveform) return;

    const parent = canvas.parentElement;
    if (!parent) return;
    const w = parent.clientWidth;
    const h = parent.clientHeight;

    // HiDPI: canvas 内部分辨率 = CSS 尺寸 × DPR
    const dpr = window.devicePixelRatio || 1;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);

    const barCount = waveform.length;
    const barW = w / barCount;
    const midY = h / 2;

    // 对称波形柱状图
    ctx.fillStyle = 'rgba(100, 160, 255, 0.35)';
    for (let i = 0; i < barCount; i++) {
      const amp = waveform[i] * midY * 0.85;
      ctx.fillRect(i * barW, midY - amp, Math.max(barW - 0.5, 0.5), amp * 2);
    }
  }, [waveform]);

  /* ── 全局快捷键 ──────────────────────────────────────────────────────────────
   *
   * 挂载在 window 上，弹窗打开期间全局可用：
   *   Space = 播放/暂停 | I = 当前时间 → 入点 | O = 当前时间 → 出点 | Esc = 关闭
   *
   * I/O 设置时检查与对侧间距 ≥ 0.1s（MIN_GAP），防止入出点重合。
   * 同时即时更新 ref 值，确保下一次拖拽/播放循环立即反映新边界。
   */

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === ' ') { e.preventDefault(); togglePlay(); return; }
      if (e.key === 'i' || e.key === 'I') {
        const t = videoRef.current?.currentTime ?? 0;
        if (t < outRef.current - 0.1) { setInPoint(t); inRef.current = t; }
      }
      if (e.key === 'o' || e.key === 'O') {
        const t = videoRef.current?.currentTime ?? 0;
        if (t > inRef.current + 0.1) { setOutPoint(t); outRef.current = t; }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, togglePlay]);

  /* ── 导出裁剪片段（异步非阻塞）─────────────────────────────────────────────
   *
   * 流程：点击导出 → 截取封面帧 → 调用 onExportAsync → 弹窗关闭
   *   1. 从当前 <video> 画面抓取一帧作为新节点封面缩略图
   *   2. 连同 inPoint / outPoint / 视频尺寸 一起传给父组件（VideoNodeView）
   *   3. 父组件负责：创建 loading 节点 → 后台 FFmpeg.wasm 裁剪 → 本地预览 → 异步上传 OSS
   */

  const handleExport = () => {
    if (!canExport) return;

    // ── 截取裁剪起始帧作为新节点封面 ──
    let thumbnail = null;
    let videoWidth = 0, videoHeight = 0;
    const vid = videoRef.current;
    if (vid && vid.videoWidth > 0) {
      videoWidth = vid.videoWidth;
      videoHeight = vid.videoHeight;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = vid.videoWidth;
        canvas.height = vid.videoHeight;
        canvas.getContext('2d').drawImage(vid, 0, 0, canvas.width, canvas.height);
        thumbnail = canvas.toDataURL('image/jpeg', 0.8);
      } catch (e) {
        console.warn('截取裁剪帧失败:', e);
      }
    }
    onExportAsync?.(inPoint, outPoint, { thumbnail, videoWidth, videoHeight });
  };

  /* ── 时间轴位置计算（秒 → 百分比，用于 CSS left/width） ────────────────────── */

  const inPct = duration > 0 ? (inPoint / duration) * 100 : 0;      // 入点 %
  const outPct = duration > 0 ? (outPoint / duration) * 100 : 100;  // 出点 %
  const playPct = duration > 0 ? (currentTime / duration) * 100 : 0; // 播放头 %
  const clipDur = outPoint - inPoint;                                // 选中片段时长

  /* ── 渲染 ───────────────────────────────────────────────────────────────────
   *
   * 通过 createPortal 渲染到 document.body，z-index 999999 确保在最上层。
   * 布局结构（flex column）：
   *   ┌─────────────────────────────────────────────────┐
   *   │ 标题栏：标题 + 快捷键提示 + ✕ 关闭按钮         │ 高度固定
   *   ├─────────────────────────────────────────────────┤
   *   │ 视频预览区（flex:1 填充剩余高度）               │
   *   │   - 导出时叠加半透明旋转动画遮罩                 │
   *   │   - 正常时显示播放/暂停按钮                      │
   *   ├─────────────────────────────────────────────────┤
   *   │ 底部控制面板                                     │ 高度固定
   *   │   ┌ 信息栏：IN / OUT / 片段时长                 │
   *   │   ├ 时间轴：波形 + 选区高亮 + IN/OUT 手柄 + 播放头│
   *   │   └ 操作按钮：入点 | 出点 | 播放 | 导出          │
   *   └─────────────────────────────────────────────────┘
   */

  return createPortal(
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.88)',
      backdropFilter: 'blur(6px)',
      zIndex: 999999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* ── 主面板 90vw × 85vh ─────────────────────────────────────────────── */}
      <div style={{
        width: '90vw', height: '85vh',
        maxWidth: '1600px',
        background: C.bg,
        borderRadius: '10px',
        border: `1px solid ${C.border}`,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 24px 80px rgba(0,0,0,0.7)',
        userSelect: 'none',
      }}>

        {/* ── 顶部标题栏 ──────────────────────────────────────────────────── */}
        <div style={{
          height: '44px', minHeight: '44px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 16px',
          background: 'var(--ed-header)',
          borderBottom: `1px solid ${C.border}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Scissors size={15} color={C.text} />
            <span style={{ color: C.textBright, fontSize: '13px', fontWeight: 600 }}>视频快剪</span>
            <span style={{ color: C.textMuted, fontSize: '11px', marginLeft: '8px' }}>
              快捷键：空格=播放/暂停 · I=设置入点 · O=设置出点 · Esc=关闭
            </span>
          </div>
          <button onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: C.textMuted, display: 'flex', alignItems: 'center', padding: '4px',
            }}
            title="关闭 (Esc)"
          >
            <X size={18} />
          </button>
        </div>

        {/* ── 视频预览区（占据大部分空间） ────────────────────────────────── */}
        <div style={{
          flex: 1, minHeight: 0,
          background: 'var(--ed-canvas)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative',
          cursor: 'pointer',
        }}
          onClick={togglePlay}
        >
          <video
            ref={videoRef}
            src={previewUrl}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            crossOrigin="anonymous"
            onLoadedMetadata={handleLoadedMetadata}
            onTimeUpdate={handleTimeUpdate}
            onEnded={() => setIsPlaying(false)}
          />
          {/* 中央播放/暂停提示 */}
          {!isPlaying && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none',
            }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                background: 'rgba(0,0,0,0.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '2px solid rgba(255,255,255,0.3)',
              }}>
                <Play size={28} color="#fff" style={{ marginLeft: '3px' }} />
              </div>
            </div>
          )}
        </div>

        {/* ── 底部控制面板 ─────────────────────────────────────────────────── */}
        <div style={{
          background: C.panel,
          borderTop: `1px solid ${C.border}`,
          padding: '12px 20px 16px',
        }}>

          {/* 第 1 行：播放控制 + 时间信息 */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            marginBottom: '10px',
          }}>
            {/* 播放按钮 */}
            <button onClick={togglePlay} style={{
              width: '32px', height: '32px', borderRadius: '6px',
              border: `1px solid ${C.border}`, background: C.surface,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: C.text, flexShrink: 0,
            }}>
              {isPlaying ? <Pause size={14} /> : <Play size={14} style={{ marginLeft: '1px' }} />}
            </button>

            {/* 当前时间 / 总时长 */}
            <div style={{
              fontFamily: 'SF Mono, Menlo, Consolas, monospace',
              fontSize: '13px', color: C.textBright, letterSpacing: '0.5px',
            }}>
              {fmt(currentTime)} <span style={{ color: C.textMuted }}>/</span> {fmt(duration)}
            </div>

            <div style={{ flex: 1 }} />

            {/* 入点/出点读数 */}
            <div style={{ display: 'flex', gap: '16px', fontSize: '12px', fontFamily: 'monospace' }}>
              <span>
                <span style={{
                  display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%',
                  background: C.inColor, marginRight: '5px', verticalAlign: 'middle',
                }} />
                <span style={{ color: C.inColor, fontWeight: 600 }}>入点</span>
                <span style={{ color: C.textMuted, marginLeft: '6px' }}>{fmt(inPoint)}</span>
              </span>
              <span>
                <span style={{
                  display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%',
                  background: C.outColor, marginRight: '5px', verticalAlign: 'middle',
                }} />
                <span style={{ color: C.outColor, fontWeight: 600 }}>出点</span>
                <span style={{ color: C.textMuted, marginLeft: '6px' }}>{fmt(outPoint)}</span>
              </span>
              <span style={{ color: C.textMuted }}>
                片段 <span style={{ color: C.textBright, fontWeight: 600 }}>{fmt(clipDur)}</span>
              </span>
            </div>
          </div>

          {/* 第 2 行：时间轴 */}
          <div style={{ position: 'relative', marginBottom: '12px' }}>
            {/* 时间轴条（增高以显示波形） */}
            <div
              ref={timelineRef}
              onMouseDown={handleBgMouseDown}
              style={{
                position: 'relative',
                height: '64px',
                background: C.surface,
                borderRadius: '6px',
                border: `1px solid ${C.border}`,
                cursor: 'pointer',
                overflow: 'visible',
              }}
            >
              {/* 音频波形 Canvas（绘制在最底层） */}
              <canvas
                ref={waveCanvasRef}
                style={{
                  position: 'absolute', top: 0, left: 0,
                  width: '100%', height: '100%',
                  borderRadius: '6px',
                  pointerEvents: 'none',
                }}
              />
              {/* 选中区域高亮 */}
              <div style={{
                position: 'absolute', top: 0, bottom: 0,
                left: `${inPct}%`,
                width: `${outPct - inPct}%`,
                background: C.selectedBg,
                borderTop: `2px solid ${C.selectedBorder}`,
                borderBottom: `2px solid ${C.selectedBorder}`,
                pointerEvents: 'none',
              }} />

              {/* 入点手柄 */}
              <div
                onMouseDown={(e) => startDrag(e, 'in')}
                style={{
                  position: 'absolute', top: '-2px', bottom: '-2px',
                  left: `${inPct}%`,
                  width: '14px',
                  transform: 'translateX(-50%)',
                  background: C.inColor,
                  borderRadius: '4px',
                  cursor: 'ew-resize',
                  zIndex: 3,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: `0 0 8px ${C.inColor}55`,
                  transition: 'box-shadow 0.15s',
                }}
                title="拖动调整入点"
              >
                {/* 手柄纹理 */}
                <div style={{
                  display: 'flex', flexDirection: 'column', gap: '3px',
                  alignItems: 'center',
                }}>
                  <div style={{ width: '4px', height: '1px', background: 'rgba(255,255,255,0.6)' }} />
                  <div style={{ width: '4px', height: '1px', background: 'rgba(255,255,255,0.6)' }} />
                  <div style={{ width: '4px', height: '1px', background: 'rgba(255,255,255,0.6)' }} />
                </div>
              </div>

              {/* 出点手柄 */}
              <div
                onMouseDown={(e) => startDrag(e, 'out')}
                style={{
                  position: 'absolute', top: '-2px', bottom: '-2px',
                  left: `${outPct}%`,
                  width: '14px',
                  transform: 'translateX(-50%)',
                  background: C.outColor,
                  borderRadius: '4px',
                  cursor: 'ew-resize',
                  zIndex: 3,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: `0 0 8px ${C.outColor}55`,
                  transition: 'box-shadow 0.15s',
                }}
                title="拖动调整出点"
              >
                <div style={{
                  display: 'flex', flexDirection: 'column', gap: '3px',
                  alignItems: 'center',
                }}>
                  <div style={{ width: '4px', height: '1px', background: 'rgba(255,255,255,0.6)' }} />
                  <div style={{ width: '4px', height: '1px', background: 'rgba(255,255,255,0.6)' }} />
                  <div style={{ width: '4px', height: '1px', background: 'rgba(255,255,255,0.6)' }} />
                </div>
              </div>

              {/* 播放头（细竖线 + 顶部三角） */}
              <div style={{
                position: 'absolute', top: 0, bottom: 0,
                left: `${playPct}%`,
                width: '2px',
                transform: 'translateX(-50%)',
                background: C.playhead,
                zIndex: 4,
                pointerEvents: 'none',
                boxShadow: `0 0 6px ${C.playhead}88`,
              }} />
              {/* 播放头顶部倒三角 */}
              <div style={{
                position: 'absolute', top: '-6px',
                left: `${playPct}%`,
                transform: 'translateX(-50%)',
                width: 0, height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderTop: `8px solid ${C.playhead}`,
                zIndex: 5,
                pointerEvents: 'none',
              }} />
            </div>
          </div>

          {/* 第 3 行：快捷按钮 + 导出 */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            {/* 快捷按钮 */}
            <ToolBtn
              onClick={() => {
                const t = videoRef.current?.currentTime ?? 0;
                if (t < outRef.current - 0.1) { setInPoint(t); inRef.current = t; }
              }}
              color={C.inColor}
            >
              设入点 [I]
            </ToolBtn>
            <ToolBtn
              onClick={() => {
                const t = videoRef.current?.currentTime ?? 0;
                if (t > inRef.current + 0.1) { setOutPoint(t); outRef.current = t; }
              }}
              color={C.outColor}
            >
              设出点 [O]
            </ToolBtn>
            <ToolBtn onClick={() => seekTo(inPoint)} color={C.inColor}>跳入点</ToolBtn>
            <ToolBtn onClick={() => seekTo(outPoint)} color={C.outColor}>跳出点</ToolBtn>

            <div style={{ flex: 1 }} />

            {/* 视频加载中提示（duration=0 时说明元数据尚未就绪） */}
            {!canExport && (
              <span style={{ color: '#f59e0b', fontSize: '11px' }}>
                ⚠ 视频正在加载，请稍候...
              </span>
            )}

            {/* 取消 */}
            <button onClick={onClose} style={{
              padding: '6px 16px', borderRadius: '6px',
              border: `1px solid ${C.border}`, background: 'transparent',
              color: C.textMuted, fontSize: '12px', cursor: 'pointer',
            }}>取消</button>

            {/* 导出 */}
            <button
              onClick={handleExport}
              disabled={!canExport || clipDur < 0.1}
              style={{
                padding: '6px 20px', borderRadius: '6px',
                background: (!canExport || clipDur < 0.1) ? '#374151' : C.accent,
                border: 'none',
                color: (!canExport || clipDur < 0.1) ? C.textMuted : '#fff',
                fontSize: '12px', fontWeight: 600,
                cursor: (!canExport || clipDur < 0.1) ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: '6px',
                transition: 'background 0.2s',
              }}
            >
              <Scissors size={13} /> 导出片段
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── 子组件 ───────────────────────────────────────────────────────────────────

/** 快捷工具按钮（底部操作栏）— 悬停时边框/文字变为对应颜色 */
function ToolBtn({ onClick, children, color }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '5px 12px', borderRadius: '5px',
        border: `1px solid ${hovered ? color : 'var(--ed-border)'}`,
        background: hovered ? `${color}18` : 'transparent',
        color: hovered ? color : 'var(--ed-text2)',
        fontSize: '11px', cursor: 'pointer',
        transition: 'all 0.15s',
        whiteSpace: 'nowrap',
      }}
    >{children}</button>
  );
}

